import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { LocalStorageUtils } from 'src/app/shared/utils/localstorage';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  appInsights: ApplicationInsights;

  private readonly applicationInsightsProperties = {
    applicationName: environment.appInsights.applicationName,
  };

  private readonly cloudRoleTagKey = 'ai.cloud.role';
  private readonly cloudRoleInstanceTagKey = 'ai.cloud.roleInstance';

  private countryCode!: string;

  constructor(private router: Router) {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        enableAutoRouteTracking: false,
      },
    });

    const localStorage = new LocalStorageUtils();
    this.countryCode = localStorage.getCurrentCountryCode();

    this.appInsights.core?.addTelemetryInitializer((telemetryItem: ITelemetryItem) => {
      const telemetryTags = (telemetryItem.tags ?? {}) as { [key: string]: string };
      telemetryTags[this.cloudRoleTagKey] = this.applicationInsightsProperties.applicationName;
      telemetryTags[this.cloudRoleInstanceTagKey] = this.applicationInsightsProperties.applicationName;
      telemetryItem.tags = telemetryTags;
    });

    this.appInsights.loadAppInsights();
  }

  private getTelemetryProperties(properties?: { [key: string]: any }) {
    return {
      latsa_organization: this.countryCode,
      ...this.applicationInsightsProperties,
      ...(properties ?? {}),
    };
  }

  logPageView(name?: string, url?: string) {
    if (!url) {
      url = this.router.url;
    }
    // option to call manually
    this.appInsights.trackPageView({
      name: name,
      uri: url,
      properties: this.getTelemetryProperties(),
    });
  }

  setAuthenticatedUserContext(userId: string, accountId?: string): void {
    if (!userId) {
      return;
    }

    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  clearAuthenticatedUserContext(): void {
    this.appInsights.clearAuthenticatedUserContext();
  }

  logEvent(eventName: string, properties?: { [key: string]: any }): void {
    this.appInsights.trackEvent({ name: eventName }, this.getTelemetryProperties(properties));
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.appInsights.trackMetric({ name: name, average: average }, this.getTelemetryProperties(properties));
  }

  logException(exception: Error, severityLevel?: number) {
    this.appInsights.trackException(
      {
        exception: exception,
        severityLevel: severityLevel,
      },
      this.getTelemetryProperties(),
    );
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights.trackTrace({ message: message }, this.getTelemetryProperties(properties));
  }
}
